import { Theme } from "@mui/material/styles";
import { BadgeColor, JobCounts } from "./types";
import { ReportJobStatus } from "../../shared/interfaces/JobStatus";
import { Job } from "../../store/jobStore";
import {
  AWAITING_INPUT_BASE,
  getAwaitingInputSurfaceStyles,
} from "@/shared/utils/awaitingInputStyles";

export const getRelativeTime = (date: Date | string | number): string => {
  const now = new Date();
  const parsedDate = new Date(date);
  const parsedMs = parsedDate.getTime();

  if (Number.isNaN(parsedMs)) return "Just now";

  const diffMs = Math.max(0, now.getTime() - parsedMs);
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 30) return "Just now";
  if (diffSec < 60) return `${diffSec} seconds ago`;
  if (diffMin === 1) return "1 minute ago";
  if (diffMin < 60) return `${diffMin} minutes ago`;
  if (diffHour === 1) return "1 hour ago";
  if (diffHour < 24) return `${diffHour} hours ago`;
  if (diffDay === 1) return "Yesterday";
  return `${diffDay} days ago`;
};

export const getJobElapsedTimeLabel = (job: Job): string =>
  getRelativeTime(job.displayStartedAt ?? job.createdAt);

export const getStatusBackground = (
  status: ReportJobStatus,
  theme: Theme,
): string => {
  switch (status) {
    case ReportJobStatus.PENDING_REVIEW:
      return "rgba(251, 140, 0, 0.10)";
    case ReportJobStatus.AWAITING_INPUT:
    case ReportJobStatus.AWAITING_TRANSCRIPT_DECISION:
      return getAwaitingInputSurfaceStyles(theme).backgroundColor;
    case ReportJobStatus.FAILED:
      return "rgba(244, 67, 54, 0.08)";
    default:
      return "rgba(38, 41, 59, 0.04)";
  }
};

export const getStatusBorderColor = (status: ReportJobStatus, theme: Theme): string => {
  switch (status) {
    case ReportJobStatus.PENDING_REVIEW:
      return "#FB8C00";
    case ReportJobStatus.AWAITING_INPUT:
    case ReportJobStatus.AWAITING_TRANSCRIPT_DECISION:
      return AWAITING_INPUT_BASE;
    case ReportJobStatus.FAILED:
      return theme.palette.error.main;
    default:
      return theme.palette.primary.main;
  }
};

export const getBadgeColor = (jobCounts: JobCounts): BadgeColor => {
  if (jobCounts.failed > 0) return "error";
  if (jobCounts.awaiting_input > 0) return "warning";
  if (jobCounts.in_progress > 0) return "primary";
  if (jobCounts.completed > 0) return "success";
  return "default";
};

export const countJobsByStatus = (jobs: Job[]): JobCounts => ({
  all: jobs.length,
  in_progress: jobs.filter(
    (j) =>
      j.status === ReportJobStatus.PENDING ||
      j.status === ReportJobStatus.STARTED ||
      j.status === ReportJobStatus.GENERATING ||
      j.status === ReportJobStatus.EXTRACTING
  ).length,
  completed: jobs.filter((j) => j.status === ReportJobStatus.PENDING_REVIEW).length,
  awaiting_input: jobs.filter(
    (j) =>
      j.status === ReportJobStatus.AWAITING_INPUT ||
      j.status === ReportJobStatus.AWAITING_TRANSCRIPT_DECISION
  ).length,
  failed: jobs.filter((j) => j.status === ReportJobStatus.FAILED).length,
});

export const isJobOpenable = (status: ReportJobStatus): boolean =>
  status === ReportJobStatus.PENDING_REVIEW ||
  status === ReportJobStatus.AWAITING_INPUT ||
  status === ReportJobStatus.AWAITING_TRANSCRIPT_DECISION ||
  // In-progress and failed jobs open the /generating/:jobId surface.
  status === ReportJobStatus.PENDING ||
  status === ReportJobStatus.STARTED ||
  status === ReportJobStatus.GENERATING ||
  status === ReportJobStatus.EXTRACTING ||
  status === ReportJobStatus.FAILED;
